/**
 * Google OAuth Service for React
 * Handles Google authentication using @react-oauth/google library
 */

import { googleLogout } from '@react-oauth/google';
import { GOOGLE_OAUTH_CONFIG } from './apiConfig.js';

class GoogleOAuthService {
    constructor() {
        this.config = GOOGLE_OAUTH_CONFIG;
    }

    /**
     * Parse JWT token to extract user information
     * @param {CredentialResponse} credentialResponse - Google credential response object
     * @returns {Object} User profile data
     */
    parseCredentialResponse(credentialResponse) {
        try {
            // Decode the JWT token from Google
            const base64Url = credentialResponse.credential.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            
            const userInfo = JSON.parse(jsonPayload);
            
            return {
                googleId: userInfo.sub,
                email: userInfo.email,
                firstName: userInfo.given_name,
                lastName: userInfo.family_name,
                profilePicture: userInfo.picture,
                emailVerified: userInfo.email_verified,
                accessToken: credentialResponse.credential // This is actually an ID token
            };
        } catch (error) {
            throw new Error('Failed to parse Google credential response');
        }
    }

    /**
     * Verify Google ID token (optional, can be done server-side)
     * @param {string} idToken - Google ID token
     * @returns {Promise<Object>} Verified user data
     */
    async verifyIdToken(idToken) {
        try {
            // For client-side verification, we can use Google's tokeninfo endpoint
            const response = await fetch(`https://oauth2.googleapis.com/tokeninfo?id_token=${idToken}`);
            
            if (!response.ok) {
                throw new Error('Token verification failed');
            }
            
            const tokenInfo = await response.json();
            
            // Verify the token is for our client
            if (tokenInfo.aud !== this.config.clientId) {
                throw new Error('Token audience mismatch');
            }
            
            return {
                googleId: tokenInfo.sub,
                email: tokenInfo.email,
                firstName: tokenInfo.given_name,
                lastName: tokenInfo.family_name,
                profilePicture: tokenInfo.picture,
                emailVerified: tokenInfo.email_verified
            };
        } catch (error) {
            console.error('Token verification failed:', error);
            throw new Error('Invalid Google token');
        }
    }

    /**
     * Sign out from Google
     */
    async signOut() {
        try {
            googleLogout();
            // Clear any stored tokens
            localStorage.removeItem('google_access_token');
        } catch (error) {
            console.error('Google sign out error:', error);
        }
    }
}

// Export singleton instance
const googleOAuthService = new GoogleOAuthService();
export default googleOAuthService;